import type { FeatureInfo, PushInfo, PushSwitch } from '@tellvo/setup-protocol';
import type { Key } from './i18n/index.js';

/**
 * What the page says about calls while the app is closed (the phone's `push` field): a note
 * in the editor, with the way out where the page has one.
 */
export interface PushNote {
  tone: 'warn' | 'bad' | 'quiet';
  key: Key;
  params: Record<string, string | number>;
  code?: string | undefined;
  action?: 'useUdp' | 'enable';
}

/**
 * The note for the open account, or null when there is nothing to act on (the service has it,
 * or it is on its way). `transport` is the editor's current choice: switched to UDP or TCP (the
 * service holds both; TLS not yet), the note says it goes over after saving.
 */
export function pushNote(push: PushInfo | undefined, transport?: string, hasSwitch = false): PushNote | null {
  if (!push) return null;
  switch (push.state) {
    case 'sent':
    case 'sending':
    case 'accountOff':
    case 'doNotDisturb':
      return null;
    case 'off':
      // A phone with the per-account switch says it on the switch's own row.
      return hasSwitch ? null : { tone: 'quiet', key: 'push.off', params: {}, action: 'enable' };
    case 'transport':
      if (transport === 'udp' || transport === 'tcp') return { tone: 'warn', key: 'push.afterSave', params: {} };
      return { tone: 'warn', key: 'push.transport', params: { transport: (push.transport ?? transport ?? '').toUpperCase() }, action: 'useUdp' };
    case 'noPassword':
      return { tone: 'warn', key: 'push.noPassword', params: {} };
    case 'noToken':
      return { tone: 'warn', key: 'push.noToken', params: {} };
    case 'failed':
      // Over the plan's limit: the per-account switch says it with the numbers.
      if (hasSwitch && push.code === 'push-limit') return null;
      return push.status === undefined
        ? { tone: 'bad', key: 'push.unreachable', params: {} }
        : { tone: 'bad', key: 'push.refused', params: { status: push.status }, code: push.code };
  }
}

/**
 * The account will not ring while the app is closed, for a reason of its own (not the feature
 * switched off for all): the list says so under its name.
 */
export function pushMissing(push: PushInfo | undefined): boolean {
  return push !== undefined && !['sent', 'sending', 'off', 'accountOff', 'doNotDisturb'].includes(push.state);
}

/**
 * The per-account push switch (#16), from the phone's `pushSwitch`. `none`: a phone before
 * client#133 (no `perAccount`), which has no such switch; the page keeps the old picture. `paid`: the plan has no
 * push, a word instead of a switch (like the Features block). Otherwise a switch, disabled when
 * it cannot be turned on, with the reason for the editor and the action that removes it.
 */
export type PushRow =
  | { kind: 'none' }
  | { kind: 'paid' }
  | { kind: 'switch'; on: boolean; disabled: boolean; reason?: { key: Key; params: Record<string, string | number>; warn: boolean }; action?: 'enable' };

export function pushRow(pushSwitch: PushSwitch | undefined, feature: FeatureInfo): PushRow {
  if (!feature.perAccount) return { kind: 'none' };
  // The push service has no word on the account yet (first sync after launch): the switch keeps
  // its place, still.
  if (!pushSwitch) return { kind: 'switch', on: false, disabled: true };
  const on = pushSwitch.on;
  // The phone's order: inactive, otherDevices, plan, featureOff, noRoom. A device out of places
  // may also see the feature unavailable; it is told so, not offered a subscription.
  if (pushSwitch.reason === 'inactive') return { kind: 'switch', on, disabled: true };
  if (pushSwitch.reason === 'otherDevices') return { kind: 'switch', on, disabled: true, reason: { key: 'push.otherDevices', params: {}, warn: true } };
  if (!feature.available || pushSwitch.reason === 'plan') return { kind: 'paid' };
  if (pushSwitch.available) return { kind: 'switch', on, disabled: false };
  switch (pushSwitch.reason) {
    case 'featureOff':
      return { kind: 'switch', on, disabled: true, reason: { key: 'push.offAll', params: {}, warn: false }, action: 'enable' };
    case 'noRoom':
      return { kind: 'switch', on, disabled: true, reason: noRoom(pushSwitch, 'push.noRoom', 'push.noRoomBare') };
    default:
      // The line itself is off: the switch right above says so.
      return { kind: 'switch', on, disabled: true };
  }
}

/** "No room", with the numbers when the phone gave them. */
export function noRoom(pushSwitch: PushSwitch, withNumbers: Key, bare: Key): { key: Key; params: Record<string, string | number>; warn: true } {
  // "0 of 0" says nothing: a plan with no places at all gets the words alone.
  return pushSwitch.used !== undefined && pushSwitch.limit !== undefined && pushSwitch.limit > 0
    ? { key: withNumbers, params: { used: pushSwitch.used, limit: pushSwitch.limit }, warn: true }
    : { key: bare, params: {}, warn: true };
}

/**
 * Whether the line rings while the app is closed, whatever the reason it does not: the feature
 * off or not in the plan, the push service without it, push off for this line, Do Not Disturb.
 */
export function ringsWhenClosed(push: PushInfo | undefined, pushSwitch: PushSwitch | undefined, feature: FeatureInfo): boolean {
  if (!feature.available || !feature.enabled) return false;
  if (pushSwitch && !pushSwitch.on) return false;
  if (push?.state === 'accountOff' || push?.state === 'doNotDisturb') return false;
  return !pushMissing(push);
}
